import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { parseTargetContext } from './targetContext';

/**
 * Inline fair-value target context under a gate card (#116, design locked
 * 2026-07-20): a 0–1 probability gauge (mkt / fv / buy-cap ticks, shaded
 * edge span), a caption line led by the always-visible traded-outcome chip,
 * and — when the doc carries them — conviction contribution bars. Pure
 * display: every element self-explains via tooltip, all data optional, and
 * nothing here may throw (the target map drifts contractually).
 */

const fmtP = (n: number) => n.toFixed(3).replace(/^0/, '');
const fmtCents = (edge: number) =>
    `${edge >= 0 ? '+' : '−'}${(Math.abs(edge) * 100).toFixed(1)}¢`;

function Tick({
    left,
    color,
    dashed,
    tip,
}: {
    left: number;
    color: string;
    dashed?: boolean;
    tip: string;
}) {
    return (
        <Tooltip title={tip} placement="top" arrow>
            {/* 18px invisible hit zone — the visible tick is 2px */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    height: '100%',
                    width: 18,
                    left: `${left * 100}%`,
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 1,
                }}
            >
                <Box
                    sx={{
                        mt: '7px',
                        height: 12,
                        width: dashed ? 0 : 2,
                        borderRadius: 0.5,
                        bgcolor: dashed ? undefined : color,
                        borderLeft: dashed ? `2px dashed ${color}` : undefined,
                    }}
                />
            </Box>
        </Tooltip>
    );
}

export function TargetStrip({
    target,
}: {
    target: Record<string, unknown> | undefined;
}) {
    const theme = useTheme();
    const ctx = (() => {
        try {
            return parseTargetContext(target);
        } catch {
            return null;
        }
    })();
    if (!ctx) return null;

    const { outcome, mkt, fv, maxBuy, minSell, weight, edge, contributions } =
        ctx;
    const edgeColor =
        edge !== null && edge < 0
            ? theme.palette.error.main
            : theme.palette.success.main;
    const showGauge = mkt !== null || fv !== null;
    // Tick labels collide for the typical few-cent edge (both centered on
    // positions a dozen px apart) — a close pair diverges outward instead:
    // the lower label ends at its tick, the higher one starts at its tick.
    // Near the axis extremes labels clamp inward so they never overflow.
    const gaugeLabels = (
        [
            mkt !== null && { p: mkt, text: `mkt ${fmtP(mkt)}` },
            fv !== null && { p: fv, text: `fv ${fmtP(fv)}` },
        ].filter(Boolean) as { p: number; text: string }[]
    ).sort((a, b) => a.p - b.p);
    const anchors = gaugeLabels.map(() => 'translateX(-50%)');
    if (gaugeLabels.length === 2 && gaugeLabels[1].p - gaugeLabels[0].p < 0.15) {
        anchors[0] = 'translateX(-100%)';
        anchors[1] = 'translateX(0)';
    }
    gaugeLabels.forEach((l, i) => {
        if (l.p < 0.08) anchors[i] = 'translateX(0)';
        if (l.p > 0.92) anchors[i] = 'translateX(-100%)';
    });

    const outcomeTip =
        'Traded outcome — the side of the market bogg wants to buy. All prices on this strip refer to this outcome.';
    const longTail = [
        minSell !== null ? `min sell ${fmtP(minSell)}` : null,
        ctx.sizeLabel ? `size ${ctx.sizeLabel}` : null,
        ctx.timestamp ? `target ${ctx.timestamp}` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns:
                    contributions.length > 0
                        ? 'minmax(240px, 1.1fr) minmax(200px, 1fr)'
                        : '1fr',
                gap: '4px 32px',
                alignItems: 'end',
                mt: 1,
                maxWidth: 640,
            }}
        >
            <Box>
                {showGauge && (
                    <Box sx={{ position: 'relative', height: 34 }}>
                        <Tooltip
                            title="Probability axis 0 → 1 — a tick's position is its price/probability for the traded outcome, comparable across all cards."
                            placement="top"
                            arrow
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: '11px',
                                    height: 4,
                                    borderRadius: 1,
                                    bgcolor: alpha(theme.palette.divider, 0.6),
                                }}
                            />
                        </Tooltip>
                        {[0, 0.5, 1].map(p => (
                            <Typography
                                key={p}
                                sx={{
                                    position: 'absolute',
                                    top: '17px',
                                    left: `${p * 100}%`,
                                    transform:
                                        p === 0
                                            ? 'none'
                                            : p === 1
                                              ? 'translateX(-100%)'
                                              : 'translateX(-50%)',
                                    fontSize: 8.5,
                                    color: 'text.disabled',
                                }}
                            >
                                {p === 0.5 ? '.5' : p}
                            </Typography>
                        ))}
                        {mkt !== null && fv !== null && (
                            <Tooltip
                                title={`Edge ${fmtCents(edge!)} — fair value minus market price: the mispricing bogg wants to capture. ${
                                    edge! >= 0
                                        ? 'Positive: the market looks cheap to the model.'
                                        : "Negative: price has run past fair value; bogg's buy cap already blocks buying here."
                                }`}
                                placement="top"
                                arrow
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '11px',
                                        height: 4,
                                        borderRadius: 1,
                                        left: `${Math.min(mkt, fv) * 100}%`,
                                        width: `${Math.abs(fv - mkt) * 100}%`,
                                        bgcolor: alpha(edgeColor, 0.55),
                                        zIndex: 1,
                                    }}
                                />
                            </Tooltip>
                        )}
                        {gaugeLabels.map((l, i) => (
                            <Typography
                                key={l.text}
                                sx={{
                                    position: 'absolute',
                                    top: '-6px',
                                    left: `${l.p * 100}%`,
                                    transform: anchors[i],
                                    fontSize: 9,
                                    letterSpacing: '.05em',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {l.text}
                            </Typography>
                        ))}
                        {mkt !== null && (
                            <Tick
                                left={mkt}
                                color={theme.palette.text.primary}
                                tip={`Market price · ${fmtP(mkt)} — what the ${outcome ?? 'traded'} outcome trades at right now.`}
                            />
                        )}
                        {maxBuy !== null && (
                            <Tick
                                left={maxBuy}
                                color={theme.palette.warning.main}
                                dashed
                                tip={`Buy cap · ${fmtP(maxBuy)} — bogg never pays more than this, whatever you approve.`}
                            />
                        )}
                        {fv !== null && (
                            <Tick
                                left={fv}
                                color={theme.palette.primary.main}
                                tip={`Fair value · ${fmtP(fv)} — the probability bogg's conviction model assigns to this outcome.`}
                            />
                        )}
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        columnGap: 0.75,
                        fontFamily: 'monospace',
                        fontSize: 11,
                        color: 'text.secondary',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {outcome && (
                        <Tooltip title={outcomeTip} placement="top" arrow>
                            <Box
                                component="span"
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    letterSpacing: '.08em',
                                    px: 0.75,
                                    py: '1px',
                                    borderRadius: 0.5,
                                    mr: 0.5,
                                    fontFamily: 'inherit',
                                    color: theme.palette.getContrastText(
                                        outcome.toLowerCase() === 'yes'
                                            ? theme.palette.success.main
                                            : theme.palette.error.main
                                    ),
                                    bgcolor:
                                        outcome.toLowerCase() === 'yes'
                                            ? theme.palette.success.main
                                            : theme.palette.error.main,
                                }}
                            >
                                {outcome.toUpperCase()}
                            </Box>
                        </Tooltip>
                    )}
                    {mkt !== null && (
                        <Tooltip
                            title="Market price — current price of the traded outcome."
                            placement="top"
                            arrow
                        >
                            <span>mkt {mkt.toFixed(3)}</span>
                        </Tooltip>
                    )}
                    {mkt !== null && fv !== null && <span>→</span>}
                    {fv !== null && (
                        <Tooltip
                            title="Fair value — the model's estimated true probability."
                            placement="top"
                            arrow
                        >
                            <span>fv {fv.toFixed(3)}</span>
                        </Tooltip>
                    )}
                    {edge !== null && (
                        <Tooltip
                            title="Edge — fair value minus market price: the mispricing bogg wants to capture."
                            placement="top"
                            arrow
                        >
                            <Box
                                component="span"
                                sx={{ color: edgeColor, fontWeight: 600 }}
                            >
                                {fmtCents(edge)} edge
                            </Box>
                        </Tooltip>
                    )}
                    {maxBuy !== null && (
                        <Tooltip
                            title="Buy cap — bogg's own maximum buy price for this market."
                            placement="top"
                            arrow
                        >
                            <span>· buy ≤ {maxBuy.toFixed(3)}</span>
                        </Tooltip>
                    )}
                    {weight !== null && (
                        <Tooltip
                            title={`Signal weight · ${weight.toFixed(2)} — how strongly this target weighs within the strategy's current signal set.${longTail ? ` (${longTail})` : ''}`}
                            placement="top"
                            arrow
                        >
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                                · w {weight.toFixed(2)}
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            {contributions.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '3px',
                    }}
                >
                    <Tooltip
                        title="Which trader groups drove this signal. Bar length = share of the combined signal; the number is that group's own conviction score."
                        placement="top"
                        arrow
                    >
                        <Typography
                            sx={{
                                fontSize: 9,
                                letterSpacing: '.07em',
                                textTransform: 'uppercase',
                                color: 'text.disabled',
                            }}
                        >
                            conviction contributions
                        </Typography>
                    </Tooltip>
                    {contributions.map((c, i) => (
                        <Tooltip
                            key={`${c.name}:${i}`}
                            title={`${c.name}${c.score !== null ? ` — score ${c.score.toFixed(2)}` : ''} · contributes ${(c.share * 100).toFixed(0)}% of the combined signal.`}
                            placement="top"
                            arrow
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '82px 1fr 34px',
                                    gap: '0 8px',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    noWrap
                                    sx={{
                                        fontSize: 10,
                                        color: 'text.secondary',
                                    }}
                                >
                                    {c.name}
                                </Typography>
                                <Box
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: alpha(
                                            theme.palette.divider,
                                            0.6
                                        ),
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${c.share * 100}%`,
                                            bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.75
                                            ),
                                        }}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: 'monospace',
                                        color: 'text.disabled',
                                        textAlign: 'right',
                                    }}
                                >
                                    {c.score !== null
                                        ? c.score.toFixed(2).replace(/^0/, '')
                                        : '—'}
                                </Typography>
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
            )}
        </Box>
    );
}
